/** Whether an address is in the list, as mail delivery reads addresses: without regard to case. */
export const includesAddress = (addresses: string[], address: string): boolean =>
  addresses.some((listed) => listed.toLowerCase() === address.toLowerCase());

/** What typed or pasted text gave: its addresses, the words not added, and those of them the field keeps to fix. */
export interface ReadRecipients {
  addresses: string[];
  notAdded: string[];
  /** The words not added that, kept together in the field, read again as exactly themselves and add nothing. */
  toFix: string[];
}

// A local part neither starts nor ends with a quote or a dot, and holds no two dots together: a quote there is the edge
// of a name, as Outlook and word processors write them.
const LOCAL_EDGE = '[\\p{L}\\p{N}!#$%&*+/=?^_`{|}~-]';
const LOCAL_INNER = "[\\p{L}\\p{N}!#$%&'*+/=?^_`{|}~.-]";
// A domain label starts and ends with a letter or a digit, as a host name must; a hyphen goes only between them.
const DOMAIN_LABEL = '[\\p{L}\\p{N}](?:[\\p{L}\\p{N}-]*[\\p{L}\\p{N}])?';
const ADDRESS = new RegExp(
  `^(?!.*\\.\\.)${LOCAL_EDGE}(?:${LOCAL_INNER}*${LOCAL_EDGE})?@${DOMAIN_LABEL}(?:\\.${DOMAIN_LABEL})+$`,
  'u'
);
// The full-width forms too, which a CJK keyboard types.
const SEPARATORS = new Set([',', ';', '\n', '\r', '\uFF0C', '\uFF1B']);
// Zero-width and other format characters arrive with pasted text and would make one address two different strings.
const INVISIBLE = /\p{Cf}/gu;

const normalized = (text: string): string =>
  text
    .replace(INVISIBLE, '')
    .replace(/\uFF1C/g, '<')
    .replace(/\uFF1E/g, '>');

interface Entry {
  name: string;
  bracketed: string | null;
  /** What its quoted names and comments held, which is never an address of the entry, but may need naming. */
  quoted: string[];
  commented: string[];
}

const newEntry = (): Entry => ({ name: '', bracketed: null, quoted: [], commented: [] });

interface Scan {
  entries: Entry[];
  open: boolean;
  unclosed: number[];
}

// Where one reading from left to right has got to. At most one of a quoted name, a comment and a bracket is open.
interface ScanState {
  text: string;
  /** The openers that are word breaks, as no closer follows them. */
  asBreaks: Set<number>;
  at: number;
  entries: Entry[];
  entry: Entry;
  quotedAt: number;
  comments: number[];
  /** What the open quoted name or comment holds so far. */
  inside: string;
  bracket: string | null;
  /** A quoted local part inside angle brackets holds separators and brackets as plain text, as a comment there does. */
  bracketQuoted: boolean;
  bracketComments: number;
}

const closeEntry = (state: ScanState) => {
  state.entries.push(state.entry);
  state.entry = newEntry();
};

// The character a backslash escapes, which it consumes.
const escapedChar = (state: ScanState): string => state.text[++state.at] ?? '';

const stepQuoted = (state: ScanState, char: string) => {
  if (char === '\\') state.inside += escapedChar(state);
  else if (char !== '"') state.inside += char;
  else {
    state.quotedAt = -1;
    state.entry.quoted.push(state.inside);
    state.entry.name += ' ';
  }
};

const closeComment = (state: ScanState) => {
  state.comments.pop();
  if (state.comments.length > 0) {
    state.inside += ' ';
    return;
  }
  state.entry.commented.push(state.inside);
  state.entry.name += ' ';
};

const stepComment = (state: ScanState, char: string) => {
  if (char === '\\') state.inside += escapedChar(state);
  else if (char === ')') closeComment(state);
  else if (char === '(' && !state.asBreaks.has(state.at)) {
    state.comments.push(state.at);
    state.inside += ' ';
  } else state.inside += char;
};

// Inside angle brackets, a quoted local part or a comment keeps escapes, separators and brackets as plain text.
const stepBracketInside = (state: ScanState, char: string) => {
  const bracket = state.bracket ?? '';
  if (char === '\\') {
    state.bracket = bracket + char + escapedChar(state);
    return;
  }
  if (state.bracketQuoted && char === '"') state.bracketQuoted = false;
  else if (!state.bracketQuoted && char === '(') state.bracketComments += 1;
  else if (!state.bracketQuoted && char === ')') state.bracketComments -= 1;
  state.bracket = bracket + char;
};

const stepBracket = (state: ScanState, char: string) => {
  if (state.bracketQuoted || state.bracketComments > 0) {
    stepBracketInside(state, char);
    return;
  }
  const bracket = state.bracket ?? '';
  if (char === '>') {
    state.entry.bracketed = bracket;
    state.bracket = null;
    closeEntry(state);
  } else if (char === '<' || SEPARATORS.has(char)) {
    // An address holds neither, so the bracket before it was never closed, and what it held belongs to the name.
    state.entry.name += ` ${bracket}`;
    state.bracket = char === '<' ? '' : null;
    if (state.bracket === null) closeEntry(state);
  } else if (char === '\\') state.bracket = bracket + char + escapedChar(state);
  else {
    state.bracketQuoted = char === '"';
    state.bracketComments = char === '(' ? 1 : 0;
    state.bracket = bracket + char;
  }
};

const stepOutside = (state: ScanState, char: string) => {
  const opener = char === '"' || char === '(';
  if ((opener && state.asBreaks.has(state.at)) || char === '>') state.entry.name += ' ';
  else if (opener) {
    if (char === '"') state.quotedAt = state.at;
    else state.comments.push(state.at);
    state.inside = '';
  } else if (char === '<') {
    state.bracket = '';
    state.bracketQuoted = false;
    state.bracketComments = 0;
  } else if (SEPARATORS.has(char)) closeEntry(state);
  else state.entry.name += char;
};

const step = (state: ScanState, char: string) => {
  if (state.quotedAt >= 0) stepQuoted(state, char);
  else if (state.comments.length > 0) stepComment(state, char);
  else if (state.bracket !== null) stepBracket(state, char);
  else stepOutside(state, char);
};

// One reading from left to right, in which the openers at the `asBreaks` positions are word breaks.
const scan = (text: string, asBreaks: Set<number>): Scan => {
  const state: ScanState = {
    text,
    asBreaks,
    at: 0,
    entries: [],
    entry: newEntry(),
    quotedAt: -1,
    comments: [],
    inside: '',
    bracket: null,
    bracketQuoted: false,
    bracketComments: 0
  };
  for (; state.at < text.length; state.at++) step(state, text[state.at]);
  const open = state.quotedAt >= 0 || state.comments.length > 0 || state.bracket !== null;
  // A bracket left open is part of the name, unless it ends inside a quote, which nothing reads as an address.
  if (state.bracket !== null && (state.bracketQuoted || state.bracketComments > 0)) {
    state.entry.commented.push(state.bracket);
  } else if (state.bracket !== null) state.entry.name += ` ${state.bracket}`;
  closeEntry(state);
  return { entries: state.entries, open, unclosed: state.quotedAt >= 0 ? [state.quotedAt] : state.comments };
};

/**
 * The entries of a list, read as mail reads them: a separator, a quoted name, a comment in parentheses and an address
 * in angle brackets count only outside quotes and comments; a backslash escapes the next character inside them, and
 * comments nest. A quote or parenthesis left unclosed reads as a word break.
 */
const entriesOf = (text: string): Entry[] => {
  const asBreaks = new Set<number>();
  // Each pass turns every opener it leaves unclosed into a break, so a few passes settle any text.
  for (;;) {
    const { entries, unclosed } = scan(text, asBreaks);
    if (unclosed.length === 0) return entries;
    unclosed.forEach((at) => asBreaks.add(at));
  }
};

/** Whether the text ends inside a quoted name, a comment or angle brackets, where a separator belongs to the entry. */
export const endsInsideEntry = (text: string): boolean => scan(normalized(text), new Set()).open;

// An address copied out of a mail client's header or a link, such as `[mailto:ann@example.com]`, without its wrapping.
const unwrapped = (word: string): string => word.replace(/^\[(.*)\]$/, '$1').replace(/^mailto:/i, '');

const wordsOf = (text: string): string[] => text.split(/\s+/).filter(Boolean);

/**
 * The addresses in typed or pasted text, such as a spreadsheet column or a list copied from a mail client. An entry with
 * an address in angle brackets gives that address alone, never a word of its name, and in such a list an entry without
 * brackets counts only as a single word. The words not taken that need a look come back: any that is no address, and
 * any that looks like one but sits in a name. The other words of a name are dropped.
 */
const read = (text: string): Omit<ReadRecipients, 'toFix'> => {
  const entries = entriesOf(normalized(text));
  const fromMailClient = entries.some((entry) => entry.bracketed !== null);
  const addresses: string[] = [];
  const handedBack: string[] = [];
  const take = (word: string, inName: boolean) => {
    const candidate = unwrapped(word);
    if (ADDRESS.test(candidate)) addresses.push(candidate);
    else if (!inName || candidate.includes('@')) handedBack.push(word);
  };
  entries.forEach((entry) => {
    const words = wordsOf(entry.name);
    // An address in a quoted name or a comment is never taken, but is named rather than lost without a word.
    const hidden = [...entry.quoted, ...entry.commented]
      .flatMap((text) => wordsOf(text.replace(/[<>,;]/g, ' ')))
      .filter((word) => word.includes('@'));
    if (entry.bracketed !== null) {
      if (entry.bracketed.trim()) take(entry.bracketed.trim(), false);
      handedBack.push(...words.filter((word) => word.includes('@')), ...hidden);
      return;
    }
    // A value quoted on its own, as a spreadsheet exports it, is that value rather than a name.
    if (words.length === 0 && entry.quoted.length === 1 && entry.commented.length === 0) {
      take(entry.quoted[0].trim(), false);
      return;
    }
    if (!fromMailClient) words.forEach((word) => take(word, false));
    else if (words.length === 1) take(words[0], true);
    // A comma in a name splits it off its address, so none of these words is taken.
    else handedBack.push(...words.filter((word) => word.includes('@')));
    handedBack.push(...hidden);
  });
  // A name that repeats its own address, as a contact saved without one does, leaves no address out.
  const notAdded = handedBack.filter((word) => !includesAddress(addresses, unwrapped(word)));
  return { addresses, notAdded };
};

/**
 * The addresses in typed or pasted text, with what was not added. Only the words that can never be taken when read
 * again are kept to fix: an address that sat in a name would be taken as a bare one the next time the field is read.
 */
export const recipientsIn = (text: string): ReadRecipients => {
  const { addresses, notAdded } = read(text);
  const kept = notAdded.filter((word) => read(word).addresses.length === 0);
  // Quotes and brackets pair across words, so the words must also be safe together, and stay as they are.
  const again = read(kept.join(' '));
  const stable = again.addresses.length === 0 && again.notAdded.join(' ') === kept.join(' ');
  return { addresses, notAdded, toFix: stable ? kept : [] };
};
